import React from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { IPersonaProps } from '@fluentui/react';
import {
  Field,
  MessageBar,
  MessageBarBody,
  Textarea,
  FluentProvider,
  webLightTheme,
} from '@fluentui/react-components';
import { Controller, FieldError, FieldValues, UseFormMethods, useWatch } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import ICompanyLegalEntityTermination, {
  ITerminationContact,
} from '../../../../utils/types/ICompanyLegalEntityTermination';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IDropdownOption from '../../../../utils/types/IDropdownOption';
import IFieldsetProps from '../../../../utils/types/IFieldsetProps';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import { GET_INTAKE_METHODS_QUERY } from '../../../../utils/statApi/EntityStatusApi';
import ErrorHandlingPeoplePicker from '../../../common/formFields/ErrorHandlingPeoplePicker';
import searchForUsers from '../../../../utils/microsoftGraphApi/userSearch';
import TerminationLifecycleStateDropdown from '../../../common/formFields/TerminationLifecycleStateDropdown';
import LegoCategoryDropdown from '../../../common/formFields/LegoCategoryDropdown';
import AnticipatedTerminationFYDropdown from '../../../common/formFields/AnticipatedTerminationFYDropdown';
import AnticipatedTerminationQuarterDropdown from '../../../common/formFields/AnticipatedTerminationQuarterDropdown';
import { UrlInput } from '../../../common/formFields/UrlInput';
import { TerminationLifecycleStatus } from '../../../entityTermination/types';
import { ENTITY_STATUS_TERMINATED } from '../../../../app/common/constants/EntityStatusConstants';

interface ICompanyLegalEntityTerminationFormProps {
  form: UseFormMethods<FieldValues>;
  data: ICompanyLegalEntityTermination;
}

export interface ILegoCategory {
  id: number;
  fieldName: string;
  terminationLifecycleStatusId: number;
  isSelectable: boolean;
}

const CompanyLegalEntityTerminationInfoForm = ({
  form,
  data,
}: ICompanyLegalEntityTerminationFormProps): JSX.Element => {
  const { errors: formErrors, control, setValue, getValues } = form;

  const onResolveSuggestions = (filterText: string): IPersonaProps[] | Promise<IPersonaProps[]> => {
    if (filterText) {
      return searchForUsers(filterText);
    }
    return [];
  };

  const watchTerminationLifecycleStatus = useWatch({
    control,
    name: 'terminationLifecycleStatus',
    defaultValue: data?.terminationLifecycleStatus,
  });

  const onTerminationLifecycleStatusChange = (value: IDropdownOption): void => {
    setValue('terminationLifecycleStatus', value);
    setValue('legoCategory', null);
    if (getValues('terminationLifecycleStatus')?.fieldName?.trim() === '--') {
      setValue('intakeMethod', null);
      setValue('anticipatedTerminationQuarter', null);
      setValue('anticipatedTerminationFY', null);
    }
  };

  const validateTerminationLifecycleStatus = (): string => {
    if (
      !getValues('terminationLifecycleStatus')?.fieldName?.trim() &&
      data?.isTargetForTermination
    ) {
      return 'Please select a Termination Lifecycle Status.';
    }
    return null;
  };

  const validateIntakeMethod = (): string => {
    if (
      !getValues('intakeMethod')?.fieldName?.trim() &&
      !(getValues('terminationLifecycleStatus')?.fieldName?.trim() === '--')
    ) {
      return 'Please select an intake method.';
    }
    return null;
  };

  const onQuarterChange = (value: string) => {
    setValue('anticipatedTerminationQuarter', value);
  };

  const onFYChange = (value: string) => {
    setValue('anticipatedTerminationFY', value);
  };

  const onLegoCategoryChange = (value: IDropdownOption): void => {
    setValue('legoCategory', value);
  };

  const onIntakeMethodChange = (value: IDropdownOption): void => {
    setValue('intakeMethod', value);
  };

  const IntakeMethodDropdown = (props: IFieldsetProps): JSX.Element => {
    const { value, label, required, errors, handleChange, valueKey, outputKey } = props;
    const { loading, data: intakeMethodsData } = useQuery(GET_INTAKE_METHODS_QUERY);
    const { intakeMethods } = !loading && intakeMethodsData ? intakeMethodsData : [];
    return (
      <QueryBasedDropdown
        value={value as IDropdownReference}
        label={label}
        required={required}
        outputKey={outputKey}
        dropdownOptionsData={intakeMethods}
        errors={errors}
        handleChange={handleChange}
        valueKey={valueKey}
      />
    );
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 5 }}>
        <Controller
          name="terminationLifecycleStatus"
          label="Termination Lifecycle Status"
          valueKey="terminationLifecycleStatus"
          control={control}
          defaultValue={data?.terminationLifecycleStatus}
          render={(props: { value: IDropdownReference }) => {
            return (
              <TerminationLifecycleStateDropdown
                label="Termination Lifecycle Status"
                valueKey="terminationLifecycleStatus"
                required={data?.isTargetForTermination}
                errors={formErrors}
                handleChange={onTerminationLifecycleStatusChange}
                value={props.value}
              />
            );
          }}
          rules={{
            validate: validateTerminationLifecycleStatus,
          }}
        />
        {data?.entityStatus?.fieldName === ENTITY_STATUS_TERMINATED &&
          data?.terminationLifecycleStatus?.fieldName === TerminationLifecycleStatus.COMPLETED &&
          watchTerminationLifecycleStatus?.fieldName === TerminationLifecycleStatus.IN_PROCESS && (
            <MessageBar intent="warning" layout="multiline">
              <MessageBarBody>
                This is a backward status transition. The Entity Status will be reverted from
                Terminated to Active and the Lifecycle Status will be set to Target for Termination.
                The Legal Review will be reset to Pending.
              </MessageBarBody>
            </MessageBar>
          )}
        <Controller
          name="legoCategory"
          label="Blocker Category"
          valueKey="legoCategory"
          control={control}
          defaultValue={data?.legoCategory}
          render={(props: { value: IDropdownReference }) => {
            return (
              <LegoCategoryDropdown
                label="Blocker Category"
                valueKey="legoCategory"
                errors={formErrors}
                handleChange={onLegoCategoryChange}
                value={props.value}
                watchTerminationLifecycleStatus={watchTerminationLifecycleStatus}
              />
            );
          }}
        />
        <Controller
          name="intakeMethod"
          label="Intake Method"
          valueKey="intakeMethod"
          control={control}
          defaultValue={data?.intakeMethod}
          render={(props: { value: IDropdownReference }) => {
            return (
              <IntakeMethodDropdown
                label="Intake Method"
                valueKey="intakeMethod"
                required={!(getValues('terminationLifecycleStatus')?.fieldName?.trim() === '--')}
                errors={formErrors}
                handleChange={onIntakeMethodChange}
                value={props.value}
              />
            );
          }}
          rules={{
            validate: validateIntakeMethod,
          }}
        />
        <Controller
          id="anticipatedTerminationFY"
          name="anticipatedTerminationFY"
          control={control}
          defaultValue={data?.anticipatedTerminationFY}
          render={({ value }): React.ReactElement => (
            <AnticipatedTerminationFYDropdown value={value} onChange={onFYChange} />
          )}
        />
        <Controller
          id="anticipatedTerminationQuarter"
          name="anticipatedTerminationQuarter"
          control={control}
          defaultValue={data?.anticipatedTerminationQuarter}
          render={({ value }): React.ReactElement => (
            <AnticipatedTerminationQuarterDropdown value={value} onChange={onQuarterChange} />
          )}
        />
        <UrlInput
          control={control}
          name="accountingPlanStarted"
          label="Accounting Plan Started"
          defaultValue={data?.accountingPlanStarted || ''}
          allowedDomains={[]}
          errorMessage={formErrors?.accountingPlanStarted?.message}
        />
        <Controller
          id="celaTermContact"
          name="celaTermContact"
          control={control}
          defaultValue={data?.celaTermContact}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="celaTermContact"
              labelText="CELA Termination Contact"
              onChange={(items: ITerminationContact[]) => {
                setValue('celaTermContact', items[0], { shouldDirty: true });
              }}
              itemLimit={1}
              selectedItems={value != null ? [value] : []}
              onResolveSuggestions={onResolveSuggestions}
              errorMessage={(data?.celaTermContact as unknown as FieldError)?.message}
            />
          )}
        />
        <Controller
          id="terminationContacts"
          name="terminationContacts"
          control={control}
          defaultValue={data?.terminationContacts}
          render={({ value }) => (
            <ErrorHandlingPeoplePicker
              controlName="terminationContacts"
              labelText="Termination Contacts"
              onChange={(items: ITerminationContact[]) => {
                setValue('terminationContacts', items, { shouldDirty: true });
              }}
              removeButtonAriaLabel="Remove Termination Contacts"
              onResolveSuggestions={onResolveSuggestions}
              selectedItems={value || []}
              errorMessage={(data?.terminationContacts as unknown as FieldError)?.message}
            />
          )}
        />
        <Controller
          name="legalEntityTerminationNotes"
          control={control}
          defaultValue={data?.legalEntityTerminationNotes || ''}
          render={({ onChange, value, name }): React.ReactElement => (
            <Field
              label="Notes"
              validationMessage={formErrors?.legalEntityTerminationNotes?.message}
            >
              <Textarea
                id="legalEntityTerminationNotes"
                name={name}
                value={value}
                onChange={(e, textData): void => {
                  onChange(textData.value);
                }}
                resize="vertical"
              />
            </Field>
          )}
        />
      </Stack>
    </FluentProvider>
  );
};

export default CompanyLegalEntityTerminationInfoForm;
